import { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { Input } from 'components/Input'
import { Button } from 'components/Button'

import { YupValidator } from 'services/yupValidator.service'

import { ProtocolsFormSchema } from 'store/Protocols/Protocols.validator'
import { protocolsActions } from 'store/Protocols'
import { all } from 'store/Protocols/Protocols.selector'
import { useTranslation } from 'react-i18next'

import * as S from './styled'

interface ParamTypes {
  id: string
}

const INITIAL_STATE = {
  form: {
    name: '',
    code: '',
    description: ''
  },
  errors: {
    name: '',
    code: '',
    description: ''
  }
}

export function ProtocolsForm() {
  const { t } = useTranslation()
  const { id } = useParams<ParamTypes>()
  const [form, setForm] = useState({ ...INITIAL_STATE.form })
  const [errors, setErrors] = useState({ ...INITIAL_STATE.errors })

  const { editProtocols, protocolsCreated, protocolsUpdated } = useSelector(all)

  const dispatch = useDispatch()
  const { goBack } = useHistory()

  useEffect(() => {
    if (id && editProtocols.id !== id) {
      dispatch(protocolsActions.fetchOne(id))
    }
  }, [dispatch, id, editProtocols])

  useEffect(() => {
    if (protocolsCreated.id || protocolsUpdated.id) {
      setForm(INITIAL_STATE.form)
      dispatch(protocolsActions.cleanProtocols())
      goBack()
    }
  }, [protocolsCreated, protocolsUpdated, goBack, dispatch])

  useEffect(() => {
    if (id && editProtocols.id) {
      setForm(editProtocols)
    }
  }, [id, editProtocols])

  function handleInput(value: string, name: string) {
    setErrors({ ...errors, [name]: '' })
    setForm({ ...form, [name]: value })
  }

  async function submit() {
    setErrors({ ...INITIAL_STATE.errors })

    const validator = new YupValidator(ProtocolsFormSchema())
    const [isValid, validationError] = await validator.validate(form)
    if (!isValid) {
      return setErrors(validationError as typeof INITIAL_STATE.errors)
    }

    dispatch(
      id
        ? protocolsActions.updateProtocols({ ...form, id })
        : protocolsActions.createProtocols(form)
    )
  }

  function onExit() {
    dispatch(protocolsActions.cleanProtocols())
    setForm(INITIAL_STATE.form)
    goBack()
  }

  return (
    <S.Wrapper container>
      <S.FullGrid item xs={12}>
        <div style={{ marginBottom: 16 }}>
          <S.GridHeader>
            <S.GridTitle item container xs={12}>
              <S.BoxTitle>{t('users:basicInformations')}</S.BoxTitle>
            </S.GridTitle>
          </S.GridHeader>
          <S.GridFilter container spacing={2}>
            <S.GridInput item sm={12} md={6}>
              <Input
                fullWidth
                label={t('protocols:name')}
                required
                value={form.name}
                error={!!errors.name}
                helperText={errors.name || t('common:isRequired')}
                onInput={value => handleInput(value, 'name')}
              />
            </S.GridInput>
            <S.GridInput item sm={12} md={6}>
              <Input
                fullWidth
                label={t('protocols:code')}
                required
                value={form.code}
                error={!!errors.code}
                helperText={errors.code || t('common:isRequired')}
                onInput={value => handleInput(value, 'code')}
                type="textarea"
              />
            </S.GridInput>
            <S.GridInput item sm={12} md={6}>
              <Input
                fullWidth
                label={t('protocols:description')}
                value={form.description}
                error={!!errors.description}
                helperText={errors.description}
                onInput={value => handleInput(value, 'description')}
              />
            </S.GridInput>
          </S.GridFilter>
        </div>
      </S.FullGrid>
      <S.GridButtons item xs={12}>
        <Button variant="default" onClick={onExit} size="medium">
          <p>{t('common:cancel')}</p>
        </Button>
        <Button variant="alternative" onClick={submit} size="medium">
          <p>{t('common:save')}</p>
        </Button>
      </S.GridButtons>
    </S.Wrapper>
  )
}
